// The ONLY axios layer. Components import functions from here and never call
// axios themselves, never spell an endpoint path, and never assemble a job
// payload by hand (spec §4 "hard rules").

import axios from "axios";

import { CONN_NAME, ZOHO_CONNECTION_NAME } from "./orgVariables";
import { JOB_API_VERSION, JOB_EXECUTE_IN, JOB_TYPES } from "./constants";

/* ── Base URLs ─────────────────────────────────────────────────────────────
 *
 * CRA inlines process.env at BUILD time (§12.11). A build whose environment is
 * missing a variable would otherwise emit literal "undefined/..." URLs, so
 * every base has a production fallback baked in here.
 *
 * ALL backends live on a single host: api-lb.easy-pluginz.com.au. Do not
 * reintroduce `api.easy-pluginz.com` or `api.easy-pluginz.com.au` for any of
 * them — the MySQL sibling spreads itself across both and it is a standing
 * source of 404s. One host, three paths.
 */

const API_HOST = "https://api-lb.easy-pluginz.com.au";

export const ADMIN_V2_URL =
  process.env.REACT_APP_ADMIN_SERVER_URL || `${API_HOST}/admin/v2`;

// v3 hosts the dedicated cron store. Keep this on the deployed host even in
// local dev — its cron database is not reachable from anywhere else (§1).
export const ADMIN_V3_URL =
  process.env.REACT_APP_ADMIN_SERVER_V3_URL || `${API_HOST}/admin/v3`;

export const DBSYNC_URL =
  process.env.REACT_APP_DBURL || `${API_HOST}/dbsync/postgres/v2`;

/* ── Headers ───────────────────────────────────────────────────────────── */

/**
 * Standard backend headers. `connname` is the bare extension identifier;
 * calls that act as the authenticated CRM user pass `asZohoUser: true` to get
 * the `__zoho` connection instead (§8).
 */
export const buildHeaders = ({
  orgId,
  apiKey,
  dataCenterUrl,
  accountsUrl,
  connectionUrl,
  asZohoUser = false,
} = {}) => {
  const headers = {
    orgid: orgId,
    apikey: apiKey,
    connname: asZohoUser ? ZOHO_CONNECTION_NAME : CONN_NAME,
  };
  if (dataCenterUrl) headers.datacenterurl = dataCenterUrl;
  if (accountsUrl) headers.accountsurl = accountsUrl;
  if (connectionUrl) headers.connectionUrl = connectionUrl;
  return headers;
};

/**
 * The backend signals an expired/inactive subscription as a 200 carrying
 * `{data: null, error: "<message>"}` (§8.2). That must surface to the user,
 * not be swallowed as "no results" (§12.10).
 */
export class SubscriptionError extends Error {
  constructor(message) {
    super(message);
    this.name = "SubscriptionError";
    this.isSubscriptionError = true;
  }
}

const unwrap = (response) => {
  const payload = response?.data;
  if (payload && payload.data === null && payload.error) {
    throw new SubscriptionError(payload.error);
  }
  return payload;
};

const request = async (config) => unwrap(await axios.request(config));

/* ── Admin v2 — auth ───────────────────────────────────────────────────── */

export const ZOHO_SCOPES = [
  "ZohoCRM.users.CREATE",
  "ZohoCRM.users.READ",
  "ZohoCRM.users.UPDATE",
  "ZohoCRM.org.READ",
  "ZohoCRM.org.UPDATE",
  "ZohoCRM.settings.CREATE",
  "ZohoCRM.settings.READ",
  "ZohoCRM.settings.UPDATE",
  "ZohoCRM.coql.READ",
  "ZohoCRM.modules.CREATE",
  "ZohoCRM.modules.READ",
  "ZohoCRM.modules.UPDATE",
];

export const authenticateZoho = ({
  orgId,
  apiKey,
  dataCenterUrl,
  accountsUrl,
}) =>
  request({
    url: `${ADMIN_V2_URL}/auth/zoho/authenticate`,
    method: "POST",
    headers: buildHeaders({ orgId, apiKey, dataCenterUrl, accountsUrl }),
    data: {
      scopes: ZOHO_SCOPES.join(","),
      connName: ZOHO_CONNECTION_NAME,
      redirectUrl: `${ADMIN_V2_URL}/auth/zoho/callback`,
    },
  });

export const verifyZohoAuth = ({ orgId, apiKey, dataCenterUrl, accountsUrl }) =>
  request({
    url: `${ADMIN_V2_URL}/auth/zoho/verifyauth`,
    method: "GET",
    headers: buildHeaders({
      orgId,
      apiKey,
      dataCenterUrl,
      accountsUrl,
      asZohoUser: true,
    }),
  });

export const revokeZohoAuth = ({ orgId, apiKey, dataCenterUrl, accountsUrl }) =>
  request({
    url: `${ADMIN_V2_URL}/auth/zoho/revoke`,
    method: "GET",
    headers: buildHeaders({
      orgId,
      apiKey,
      dataCenterUrl,
      accountsUrl,
      asZohoUser: true,
    }),
  });

// Auth succeeded only if a non-empty `org` array came back (§8.1).
export const isAuthVerified = (payload) =>
  Array.isArray(payload?.org) && payload.org.length > 0;

/* ── Admin v2 — widget registration ────────────────────────────────────── */

export const registerRelatedListWidget = ({
  orgId,
  apiKey,
  dataCenterUrl,
  widgetName,
  widgetUrl,
  modules,
}) =>
  request({
    url: `${ADMIN_V2_URL}/data/zoho/crm/extwidgets`,
    method: "POST",
    headers: buildHeaders({
      orgId,
      apiKey,
      dataCenterUrl,
      asZohoUser: true,
    }),
    data: { widgetType: "RELATED_LIST", name: widgetName, url: widgetUrl, modules },
  });

/* ── Admin v2 — module sync settings ───────────────────────────────────── */

// The backend keeps a pseudo-module row for the connection itself; it is never
// a user-visible module and must be filtered out of every settings list.
export const CONNECTION_SCHEMA = "ConnectionSchema";

export const getSettings = async ({ orgId, apiKey, moduleApiName }) => {
  const payload = await request({
    url: `${ADMIN_V2_URL}/db/planetscale/clients/settings`,
    method: "GET",
    params: moduleApiName ? { moduleApiName } : undefined,
    headers: buildHeaders({ orgId, apiKey }),
  });
  return payload?.data || [];
};

export const getModuleSettings = async (args) => {
  const rows = await getSettings(args);
  return rows
    .filter((row) => row?.module_name !== CONNECTION_SCHEMA)
    .map((row) => ({
      ...row,
      setting_schema:
        typeof row.setting_schema === "string"
          ? JSON.parse(row.setting_schema)
          : row.setting_schema,
    }));
};

// POST upserts: passing settingId updates, omitting it creates (§8.1).
export const saveSetting = ({
  orgId,
  apiKey,
  moduleApiName,
  settingSchema,
  settingId,
}) =>
  request({
    url: `${ADMIN_V2_URL}/db/planetscale/clients/settings`,
    method: "POST",
    headers: buildHeaders({ orgId, apiKey }),
    data: {
      moduleApiName,
      settingSchema:
        typeof settingSchema === "string"
          ? settingSchema
          : JSON.stringify(settingSchema),
      ...(settingId ? { settingId } : {}),
    },
  });

export const deleteSetting = ({ orgId, apiKey, settingId }) =>
  request({
    url: `${ADMIN_V2_URL}/db/planetscale/clients/settings/${settingId}`,
    method: "DELETE",
    headers: buildHeaders({ orgId, apiKey }),
  });

/* ── Admin v3 — dedicated cron store ───────────────────────────────────────
 *
 * §12.5: scheduling is the DEDICATED v3 store only — not the old v2
 * `/utils/cronjobs`, and not the shared `/utils/cronjoborg`. Each extension
 * gets its own segment so the stores stay isolated (MySQL uses
 * `/utils/mysqlsync/cronjoborg`).
 *
 * A job's EXISTENCE is its "In Progress" status — workers delete their own job
 * when finished, and the UI's "Stop" is a DELETE. There is no stopped state.
 */

// Confirm the exact segment with the backend (§1) — it is the one value here
// that was specified as provisional.
export const CRON_URL = `${ADMIN_V3_URL}/utils/postgressync/cronjoborg`;

export const listCronJobs = async ({ orgId, apiKey }) => {
  const payload = await request({
    url: `${CRON_URL}`,
    method: "GET",
    headers: buildHeaders({ orgId, apiKey }),
  });
  return payload?.data || [];
};

export const getCronJob = ({ orgId, apiKey, jobId }) =>
  request({
    url: `${CRON_URL}/${jobId}`,
    method: "GET",
    headers: buildHeaders({ orgId, apiKey }),
  });

// Returns the new job_id in `data`.
export const createCronJob = ({ orgId, apiKey, job }) =>
  request({
    url: `${CRON_URL}`,
    method: "POST",
    headers: buildHeaders({ orgId, apiKey }),
    data: job,
  });

export const updateCronJob = ({ orgId, apiKey, jobId, patch }) =>
  request({
    url: `${CRON_URL}/${jobId}`,
    method: "PUT",
    headers: buildHeaders({ orgId, apiKey }),
    data: patch,
  });

export const deleteCronJob = ({ orgId, apiKey, jobId }) =>
  request({
    url: `${CRON_URL}/${jobId}`,
    method: "DELETE",
    headers: buildHeaders({ orgId, apiKey }),
  });

/**
 * Build a v3 job payload. Spec §8.2 — every line here is load-bearing:
 *
 *  - `name` is EXACTLY `{orgid}__{connname}`, no module or type suffix. The
 *    list endpoint matches on it.
 *  - `connname` MUST be in `headers`; the list scopes by
 *    `headers.orgid + headers.connname`. A job stored without it is invisible
 *    to the org forever (§12.12).
 *  - `url` carries NO job_id/execute_in query tokens, and `params` carries
 *    ONLY `execute_in` (§12.13). The runner appends `&job_id=` to the url
 *    itself at fire time — which is why the url MUST already contain a `?`.
 *    The zapikey query string guarantees that, so do not "tidy" it away.
 *  - `connectionUrl` goes in `body` so the worker takes the fast path instead
 *    of re-reading the org variable.
 *  - `apiVersion` is per-function: v7 for backfill, v2 otherwise (§12.9).
 */
export const buildSyncJobOrg = ({
  orgId,
  apiKey,
  dataCenterUrl,
  zapiKey,
  functionName,
  moduleName,
  type,
  connectionUrl,
  executeIn,
  extraBody = {},
}) => {
  const apiVersion = JOB_API_VERSION[type] || "v2";
  const execute_in = Number(executeIn ?? JOB_EXECUTE_IN[type] ?? 1);

  return {
    url: `${dataCenterUrl}/crm/${apiVersion}/functions/${functionName}/actions/execute?auth_type=apikey&zapikey=${zapiKey}`,
    headers: {
      orgid: orgId,
      apikey: apiKey,
      connname: CONN_NAME,
      datacenterurl: dataCenterUrl,
    },
    body: { moduleName, type, connectionUrl, ...extraBody },
    // ONLY execute_in — the runner appends &job_id= to the url (§12.13)
    params: { execute_in },
    type: "GET",
    name: `${orgId}__${CONN_NAME}`,
    execute_in,
  };
};

// The id field of a stored v3 job row. The spec pins the payload shape but
// not the row's id key, so the fallback chain lives HERE and nowhere else —
// confirm against a real list response and collapse to one key.
export const cronJobId = (job) => job?.job_id ?? job?.id ?? job?.setting_id;

// Jobs belonging to one module, regardless of type.
export const jobsForModule = (jobs, moduleName) =>
  (jobs || []).filter((job) => job?.body?.moduleName === moduleName);

/**
 * Is this row a recurring sync job? Not every stored row carries body.type —
 * rows created before the v3 migration have a legacy shape (moduleName but
 * no type). Those are still recognizably syncs: they have no cv_id and run
 * on a multi-minute cadence, whereas backfill/restore always run at 1.
 */
export const isRecurringSyncJob = (job) => {
  const body = job?.body ?? job?.req_body;
  if (!body) return false; // truly unreadable → keep visible (§11)
  if (body.type) return body.type === JOB_TYPES.SYNC;
  if (body.cv_id) return false; // legacy backfill
  const cadence = Number(
    job?.execute_in ?? job?.executeIn ?? job?.repeat_at_min
  );
  return cadence !== 1; // untyped, no cv, not a 1-minute runner → legacy sync
};

// Backfills, typed or legacy (untyped rows with a cv_id).
export const isBackfillJob = (job) =>
  job?.body?.type === JOB_TYPES.BACKFILL ||
  (!job?.body?.type && Boolean((job?.body ?? job?.req_body)?.cv_id));

// The Historical Data tab lists everything EXCEPT the recurring sync jobs
// (§11). Rows that cannot be read at all stay visible so they remain
// deletable; rows recognizable as syncs are excluded even without body.type.
export const historicalJobs = (jobs) =>
  (jobs || []).filter((job) => !isRecurringSyncJob(job));

/* ── DB Sync (Postgres) ────────────────────────────────────────────────── */

export const verifyConnection = ({ orgId, apiKey, connectionUrl }) =>
  request({
    url: `${DBSYNC_URL}/verify`,
    method: "POST",
    headers: buildHeaders({ orgId, apiKey }),
    data: { Connection_Url: connectionUrl },
  });

export const isConnectionVerified = (payload) => payload?.data === true;

// Creates or alters the module's table from the saved setting.
export const createModuleTable = ({
  orgId,
  apiKey,
  moduleApiName,
  connectionUrl,
}) =>
  request({
    url: `${DBSYNC_URL}/setting/${moduleApiName}`,
    method: "POST",
    headers: buildHeaders({ orgId, apiKey, connectionUrl }),
    data: {},
  });

export const deleteModuleTable = ({
  orgId,
  apiKey,
  moduleApiName,
  connectionUrl,
}) =>
  request({
    url: `${DBSYNC_URL}/setting/${moduleApiName}`,
    method: "DELETE",
    headers: buildHeaders({ orgId, apiKey, connectionUrl }),
  });

// Counts the BACKUP/history table for a time range. Never a measure of sync
// health — 0 right after a fresh sync is normal and expected (§12.4).
export const countTotalRecords = ({
  orgId,
  apiKey,
  moduleName,
  timeRange,
  connectionUrl,
}) =>
  request({
    url: `${DBSYNC_URL}/count_total_record`,
    method: "POST",
    headers: buildHeaders({ orgId, apiKey, connectionUrl }),
    data: { moduleName, timeRange, connectionUrl },
  });

export const grabRecordsBasedOnTime = ({
  orgId,
  apiKey,
  moduleName,
  timeRange,
  limit,
  offset,
  connectionUrl,
}) =>
  request({
    url: `${DBSYNC_URL}/grab_records_based_on_time`,
    method: "POST",
    headers: buildHeaders({ orgId, apiKey, connectionUrl }),
    data: { moduleName, timeRange, limit, offset, connectionUrl },
  });

// Every stored version of one record — the related-list history source.
export const getRecordVersions = ({
  orgId,
  apiKey,
  moduleName,
  recordId,
  connectionUrl,
}) =>
  request({
    url: `${DBSYNC_URL}/${moduleName}/${recordId}`,
    method: "GET",
    headers: buildHeaders({ orgId, apiKey, connectionUrl }),
  });
